import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import type {
  TreatmentPlanCareTeamMember,
  TreatmentPlanConfigResponse,
  TreatmentPlanEducation,
  TreatmentPlanEscalationRule,
  TreatmentPlanFollowUp,
  TreatmentPlanGoal,
  TreatmentPlanIntervention,
  TreatmentPlanMedication,
  TreatmentPlanMonitoring,
  TreatmentPlanTemplate,
} from '../../lib/treatmentPlans';
import { useI18n } from '../../i18n/LanguageContext';
import { SelectMenu } from '../../ui/SelectMenu';

type EditableTemplate = {
  code: string;
  name: string;
  description: string;
  category: string;
  tagsText: string;
  version: number;
  active: boolean;
  estimatedDurationDays: string;
  reviewCadenceDays: string;
  defaultPatientInstructions: string;
  defaultClinicianNotes: string;
  goals: TreatmentPlanGoal[];
  interventions: TreatmentPlanIntervention[];
  medications: TreatmentPlanMedication[];
  monitoring: TreatmentPlanMonitoring[];
  followUps: TreatmentPlanFollowUp[];
  education: TreatmentPlanEducation[];
  escalationRules: TreatmentPlanEscalationRule[];
  careTeam: TreatmentPlanCareTeamMember[];
};

function normalizeCode(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toEditable(template?: TreatmentPlanTemplate): EditableTemplate {
  if (!template) {
    return {
      code: '',
      name: '',
      description: '',
      category: '',
      tagsText: '',
      version: 1,
      active: true,
      estimatedDurationDays: '',
      reviewCadenceDays: '',
      defaultPatientInstructions: '',
      defaultClinicianNotes: '',
      goals: [],
      interventions: [],
      medications: [],
      monitoring: [],
      followUps: [],
      education: [],
      escalationRules: [],
      careTeam: [],
    };
  }

  return {
    code: template.code,
    name: template.name,
    description: template.description || '',
    category: template.category || '',
    tagsText: (template.tags || []).join(', '),
    version: template.version || 1,
    active: Boolean(template.active),
    estimatedDurationDays: template.estimatedDurationDays ? String(template.estimatedDurationDays) : '',
    reviewCadenceDays: template.reviewCadenceDays ? String(template.reviewCadenceDays) : '',
    defaultPatientInstructions: template.defaultPatientInstructions || '',
    defaultClinicianNotes: template.defaultClinicianNotes || '',
    goals: (template.goals || []).map((item) => ({ ...item })),
    interventions: (template.interventions || []).map((item) => ({ ...item })),
    medications: (template.medications || []).map((item) => ({ ...item })),
    monitoring: (template.monitoring || []).map((item) => ({ ...item })),
    followUps: (template.followUps || []).map((item) => ({ ...item })),
    education: (template.education || []).map((item) => ({ ...item })),
    escalationRules: (template.escalationRules || []).map((item) => ({ ...item })),
    careTeam: (template.careTeam || []).map((item) => ({ ...item })),
  };
}

function parseNumber(value: string) {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function hasText(value?: string | null) {
  return Boolean(value && value.trim());
}

function normalizeGoals(items: TreatmentPlanGoal[]) {
  return items
    .map((item) => ({
      ...item,
      title: item.title.trim(),
      description: item.description?.trim() || undefined,
      metric: item.metric?.trim() || undefined,
      targetValue: item.targetValue?.trim() || undefined,
      unit: item.unit?.trim() || undefined,
      timeframeDays: item.timeframeDays,
    }))
    .filter((item) => hasText(item.title) || hasText(item.description) || hasText(item.metric) || hasText(item.targetValue) || hasText(item.unit) || item.timeframeDays !== undefined);
}

function normalizeInterventions(items: TreatmentPlanIntervention[]) {
  return items
    .map((item) => ({
      ...item,
      summary: item.summary.trim(),
      type: item.type?.trim() || undefined,
      frequency: item.frequency?.trim() || undefined,
      ownerRole: item.ownerRole?.trim() || undefined,
      instructions: item.instructions?.trim() || undefined,
      durationDays: item.durationDays,
    }))
    .filter((item) => hasText(item.summary) || hasText(item.type) || hasText(item.frequency) || hasText(item.ownerRole) || hasText(item.instructions) || item.durationDays !== undefined);
}

function normalizeMedications(items: TreatmentPlanMedication[]) {
  return items
    .map((item) => ({
      ...item,
      medication: item.medication.trim(),
      dose: item.dose?.trim() || undefined,
      route: item.route?.trim() || undefined,
      frequency: item.frequency?.trim() || undefined,
      indication: item.indication?.trim() || undefined,
      instructions: item.instructions?.trim() || undefined,
      durationDays: item.durationDays,
      prn: item.prn ? true : undefined,
    }))
    .filter((item) => hasText(item.medication) || hasText(item.dose) || hasText(item.route) || hasText(item.frequency) || hasText(item.indication) || hasText(item.instructions) || item.durationDays !== undefined || item.prn);
}

function normalizeMonitoring(items: TreatmentPlanMonitoring[]) {
  return items
    .map((item) => ({
      ...item,
      metric: item.metric.trim(),
      targetRange: item.targetRange?.trim() || undefined,
      frequency: item.frequency?.trim() || undefined,
      alertThreshold: item.alertThreshold?.trim() || undefined,
      actionIfOutOfRange: item.actionIfOutOfRange?.trim() || undefined,
    }))
    .filter((item) => hasText(item.metric) || hasText(item.targetRange) || hasText(item.frequency) || hasText(item.alertThreshold) || hasText(item.actionIfOutOfRange));
}

function normalizeFollowUps(items: TreatmentPlanFollowUp[]) {
  return items
    .map((item) => ({
      ...item,
      modality: item.modality.trim(),
      ownerRole: item.ownerRole?.trim() || undefined,
      purpose: item.purpose?.trim() || undefined,
      intervalDays: item.intervalDays,
    }))
    .filter((item) => hasText(item.modality) || hasText(item.ownerRole) || hasText(item.purpose) || item.intervalDays !== undefined);
}

function normalizeEducation(items: TreatmentPlanEducation[]) {
  return items
    .map((item) => ({
      ...item,
      topic: item.topic.trim(),
      objective: item.objective?.trim() || undefined,
      materials: item.materials?.trim() || undefined,
      required: item.required ? true : undefined,
    }))
    .filter((item) => hasText(item.topic) || hasText(item.objective) || hasText(item.materials) || item.required);
}

function normalizeEscalationRules(items: TreatmentPlanEscalationRule[]) {
  return items
    .map((item) => ({
      ...item,
      trigger: item.trigger.trim(),
      action: item.action.trim(),
      severity: item.severity || undefined,
    }))
    .filter((item) => hasText(item.trigger) || hasText(item.action) || hasText(item.severity));
}

function normalizeCareTeam(items: TreatmentPlanCareTeamMember[]) {
  return items
    .map((item) => ({
      ...item,
      role: item.role.trim(),
      memberName: item.memberName?.trim() || undefined,
      responsibility: item.responsibility?.trim() || undefined,
      contact: item.contact?.trim() || undefined,
    }))
    .filter((item) => hasText(item.role) || hasText(item.memberName) || hasText(item.responsibility) || hasText(item.contact));
}

function fromEditable(draft: EditableTemplate): TreatmentPlanTemplate {
  const code = normalizeCode(draft.code || draft.name);
  if (!code) throw new Error('Template code is required.');
  if (!draft.name.trim()) throw new Error('Template name is required.');

  return {
    code,
    name: draft.name.trim(),
    description: draft.description.trim() || undefined,
    category: draft.category.trim() || undefined,
    tags: [...new Set(draft.tagsText.split(',').map((item) => item.trim()).filter(Boolean))],
    version: Number.isFinite(Number(draft.version)) ? Math.max(1, Number(draft.version)) : 1,
    active: Boolean(draft.active),
    estimatedDurationDays: parseNumber(draft.estimatedDurationDays),
    reviewCadenceDays: parseNumber(draft.reviewCadenceDays),
    defaultPatientInstructions: draft.defaultPatientInstructions.trim() || undefined,
    defaultClinicianNotes: draft.defaultClinicianNotes.trim() || undefined,
    goals: normalizeGoals(draft.goals),
    interventions: normalizeInterventions(draft.interventions),
    medications: normalizeMedications(draft.medications),
    monitoring: normalizeMonitoring(draft.monitoring),
    followUps: normalizeFollowUps(draft.followUps),
    education: normalizeEducation(draft.education),
    escalationRules: normalizeEscalationRules(draft.escalationRules),
    careTeam: normalizeCareTeam(draft.careTeam),
  };
}

export default function SettingsTreatmentPlans() {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { tr } = useI18n();
  const [templates, setTemplates] = useState<TreatmentPlanTemplate[]>([]);
  const [draft, setDraft] = useState<EditableTemplate>(toEditable());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedCode = useMemo(() => normalizeCode(draft.code), [draft.code]);

  function updateGoal(index: number, patch: Partial<TreatmentPlanGoal>) {
    setDraft((prev) => ({ ...prev, goals: prev.goals.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateIntervention(index: number, patch: Partial<TreatmentPlanIntervention>) {
    setDraft((prev) => ({ ...prev, interventions: prev.interventions.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateMedication(index: number, patch: Partial<TreatmentPlanMedication>) {
    setDraft((prev) => ({ ...prev, medications: prev.medications.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateMonitoring(index: number, patch: Partial<TreatmentPlanMonitoring>) {
    setDraft((prev) => ({ ...prev, monitoring: prev.monitoring.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateFollowUp(index: number, patch: Partial<TreatmentPlanFollowUp>) {
    setDraft((prev) => ({ ...prev, followUps: prev.followUps.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateEducation(index: number, patch: Partial<TreatmentPlanEducation>) {
    setDraft((prev) => ({ ...prev, education: prev.education.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateEscalationRule(index: number, patch: Partial<TreatmentPlanEscalationRule>) {
    setDraft((prev) => ({ ...prev, escalationRules: prev.escalationRules.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  function updateCareTeamMember(index: number, patch: Partial<TreatmentPlanCareTeamMember>) {
    setDraft((prev) => ({ ...prev, careTeam: prev.careTeam.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }));
  }

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadTemplates() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<TreatmentPlanConfigResponse>('/treatment-plans/templates');
      const rows = Array.isArray(data.templates) ? data.templates : [];
      setTemplates(rows);
      setDraft(toEditable(rows[0]));
    } catch (err: any) {
      setError(err?.message || tr('Failed to load templates.'));
    } finally {
      setLoading(false);
    }
  }

  function saveDraftLocally() {
    try {
      const normalized = fromEditable(draft);
      setTemplates((prev) => {
        const next = [...prev];
        const idx = next.findIndex((item) => item.code === normalized.code);
        if (idx >= 0) next[idx] = normalized;
        else next.push(normalized);
        return next;
      });
      setDraft(toEditable(normalized));
      setError(null);
      setSuccess(tr('Template staged locally.'));
    } catch (err: any) {
      setError(err?.message || tr('Validation failed.'));
    }
  }

  function removeTemplate(code: string) {
    setTemplates((prev) => prev.filter((item) => item.code !== code));
    if (selectedCode === code) setDraft(toEditable());
  }

  async function saveAll() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { templates: templates.map((template) => fromEditable(toEditable(template))) };
      const response = await fetchApi('/treatment-plans/templates', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Save failed (${response.status})`);
      setSuccess(tr('Treatment plan templates saved.'));
      await loadTemplates();
    } catch (err: any) {
      setError(err?.message || tr('Failed to save templates.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tr('Treatment Plans')}</h1>
            <p className="text-slate-500 text-sm">{tr('Define reusable treatment-plan templates used in patient charts.')}</p>
          </div>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white disabled:opacity-60" onClick={saveAll} disabled={saving || loading}>
          <Save size={16} /> {saving ? tr('Saving...') : tr('Save Templates')}
        </button>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}
      {success ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div> : null}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4">
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">{tr('Templates')}</h2>
            <button className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-sm" onClick={() => setDraft(toEditable())}><Plus size={14} /> {tr('New')}</button>
          </div>
          {loading ? <p className="text-sm text-slate-500">{tr('Loading templates...')}</p> : (
            <div className="space-y-2">
              {templates.map((template) => (
                <div key={template.code} className={`rounded-lg border px-3 py-2 ${selectedCode === template.code ? 'border-ink bg-slate-50' : 'border-slate-200'}`}>
                  <button className="w-full text-left" onClick={() => setDraft(toEditable(template))}>
                    <p className="font-medium text-slate-900">{template.name}</p>
                    <p className="text-xs text-slate-500">{template.code}</p>
                  </button>
                </div>
              ))}
              {!templates.length ? <p className="text-sm text-slate-500">{tr('No templates configured yet.')}</p> : null}
            </div>
          )}
        </div>

        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">{tr('Template Builder')}</h2>
            {selectedCode ? <button className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs text-rose-700" onClick={() => removeTemplate(selectedCode)}><Trash2 size={12} /> {tr('Remove')}</button> : null}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-lime-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Template Summary')}</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">{draft.name.trim() || tr('Untitled template')}</h3>
                <p className="mt-1 text-sm text-slate-600">{draft.description.trim() || tr('Build reusable defaults for patient treatment plans without editing raw JSON.')}</p>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] font-medium">
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-700">{selectedCode || tr('No code yet')}</span>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-700">{tr('Version')} {draft.version}</span>
                <span className={`rounded-full px-2.5 py-1 ${draft.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>{draft.active ? tr('Active') : tr('Inactive')}</span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
              <MetricCard label={tr('Goals')} value={String(draft.goals.length)} detail={tr('Structured outcome targets')} />
              <MetricCard label={tr('Interventions')} value={String(draft.interventions.length)} detail={tr('Reusable care actions')} />
              <MetricCard label={tr('Monitoring')} value={String(draft.monitoring.length)} detail={tr('Follow-up checkpoints')} />
              <MetricCard label={tr('Care Team')} value={String(draft.careTeam.length)} detail={tr('Roles and ownership')} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Input label="Code" value={draft.code} onChange={(value) => setDraft((prev) => ({ ...prev, code: normalizeCode(value) }))} />
            <Input label="Name" value={draft.name} onChange={(value) => setDraft((prev) => ({ ...prev, name: value }))} />
            <Input label="Category" value={draft.category} onChange={(value) => setDraft((prev) => ({ ...prev, category: value }))} />
            <Input label="Tags (comma separated)" value={draft.tagsText} onChange={(value) => setDraft((prev) => ({ ...prev, tagsText: value }))} />
            <Input label="Version" type="number" value={String(draft.version)} onChange={(value) => setDraft((prev) => ({ ...prev, version: Number(value) || 1 }))} />
            <Input label="Estimated Duration Days" type="number" value={draft.estimatedDurationDays} onChange={(value) => setDraft((prev) => ({ ...prev, estimatedDurationDays: value }))} />
            <Input label="Review Cadence Days" type="number" value={draft.reviewCadenceDays} onChange={(value) => setDraft((prev) => ({ ...prev, reviewCadenceDays: value }))} />
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-7"><input type="checkbox" checked={draft.active} onChange={(event) => setDraft((prev) => ({ ...prev, active: event.target.checked }))} /> {tr('Active')}</label>
          </div>
          <TextArea label="Description" value={draft.description} onChange={(value) => setDraft((prev) => ({ ...prev, description: value }))} />
          <TextArea label="Default Patient Instructions" value={draft.defaultPatientInstructions} onChange={(value) => setDraft((prev) => ({ ...prev, defaultPatientInstructions: value }))} />
          <TextArea label="Default Clinician Notes" value={draft.defaultClinicianNotes} onChange={(value) => setDraft((prev) => ({ ...prev, defaultClinicianNotes: value }))} />

          <details open className="rounded-xl border border-slate-200 bg-white overflow-hidden group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-900">
              <div>
                <div>{tr('Core Plan Structure')}</div>
                <div className="mt-1 text-xs font-normal text-slate-500">{tr('Define the main clinical intent clinicians should start with.')}</div>
              </div>
              <span className="text-xs text-slate-500 group-open:hidden">{tr('Expand')}</span>
              <span className="hidden text-xs text-slate-500 group-open:inline">{tr('Collapse')}</span>
            </summary>
            <div className="space-y-4 border-t border-slate-100 p-4">
              <Section title={tr('Goals')} onAdd={() => setDraft((prev) => ({ ...prev, goals: [...prev.goals, { title: '', metric: '', targetValue: '' }] }))}>
                {draft.goals.map((item, index) => (
                  <Row key={`goal-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, goals: prev.goals.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Title')} value={item.title || ''} onChange={(value) => updateGoal(index, { title: value })} />
                    <Input label={tr('Metric')} value={item.metric || ''} onChange={(value) => updateGoal(index, { metric: value })} />
                    <Input label={tr('Target Value')} value={item.targetValue || ''} onChange={(value) => updateGoal(index, { targetValue: value })} />
                    <Input label={tr('Unit')} value={item.unit || ''} onChange={(value) => updateGoal(index, { unit: value })} />
                    <Input label={tr('Timeframe Days')} type="number" value={item.timeframeDays ? String(item.timeframeDays) : ''} onChange={(value) => updateGoal(index, { timeframeDays: parseNumber(value) })} />
                    <TextArea label={tr('Description')} value={item.description || ''} onChange={(value) => updateGoal(index, { description: value })} />
                  </Row>
                ))}
              </Section>

              <Section title={tr('Interventions')} onAdd={() => setDraft((prev) => ({ ...prev, interventions: [...prev.interventions, { summary: '', type: '', frequency: '' }] }))}>
                {draft.interventions.map((item, index) => (
                  <Row key={`intervention-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, interventions: prev.interventions.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Summary')} value={item.summary || ''} onChange={(value) => updateIntervention(index, { summary: value })} />
                    <Input label={tr('Type')} value={item.type || ''} onChange={(value) => updateIntervention(index, { type: value })} />
                    <Input label={tr('Frequency')} value={item.frequency || ''} onChange={(value) => updateIntervention(index, { frequency: value })} />
                    <Input label={tr('Duration Days')} type="number" value={item.durationDays ? String(item.durationDays) : ''} onChange={(value) => updateIntervention(index, { durationDays: parseNumber(value) })} />
                    <Input label={tr('Owner Role')} value={item.ownerRole || ''} onChange={(value) => updateIntervention(index, { ownerRole: value })} />
                    <TextArea label={tr('Instructions')} value={item.instructions || ''} onChange={(value) => updateIntervention(index, { instructions: value })} />
                  </Row>
                ))}
              </Section>

              <Section title={tr('Monitoring')} onAdd={() => setDraft((prev) => ({ ...prev, monitoring: [...prev.monitoring, { metric: '', targetRange: '' }] }))}>
                {draft.monitoring.map((item, index) => (
                  <Row key={`monitor-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, monitoring: prev.monitoring.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Metric')} value={item.metric || ''} onChange={(value) => updateMonitoring(index, { metric: value })} />
                    <Input label={tr('Target Range')} value={item.targetRange || ''} onChange={(value) => updateMonitoring(index, { targetRange: value })} />
                    <Input label={tr('Frequency')} value={item.frequency || ''} onChange={(value) => updateMonitoring(index, { frequency: value })} />
                    <Input label={tr('Alert Threshold')} value={item.alertThreshold || ''} onChange={(value) => updateMonitoring(index, { alertThreshold: value })} />
                    <TextArea label={tr('Action If Out Of Range')} value={item.actionIfOutOfRange || ''} onChange={(value) => updateMonitoring(index, { actionIfOutOfRange: value })} />
                  </Row>
                ))}
              </Section>
            </div>
          </details>

          <details className="rounded-xl border border-slate-200 bg-white overflow-hidden group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-900">
              <div>
                <div>{tr('Advanced Defaults')}</div>
                <div className="mt-1 text-xs font-normal text-slate-500">{tr('Use these when the template needs medication, escalation, education, or ownership detail.')}</div>
              </div>
              <span className="text-xs text-slate-500 group-open:hidden">{tr('Expand')}</span>
              <span className="hidden text-xs text-slate-500 group-open:inline">{tr('Collapse')}</span>
            </summary>
            <div className="space-y-4 border-t border-slate-100 p-4">
              <Section title={tr('Medications')} onAdd={() => setDraft((prev) => ({ ...prev, medications: [...prev.medications, { medication: '', dose: '', frequency: '' }] }))}>
                {draft.medications.map((item, index) => (
                  <Row key={`medication-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, medications: prev.medications.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Medication')} value={item.medication || ''} onChange={(value) => updateMedication(index, { medication: value })} />
                    <Input label={tr('Dose')} value={item.dose || ''} onChange={(value) => updateMedication(index, { dose: value })} />
                    <Input label={tr('Route')} value={item.route || ''} onChange={(value) => updateMedication(index, { route: value })} />
                    <Input label={tr('Frequency')} value={item.frequency || ''} onChange={(value) => updateMedication(index, { frequency: value })} />
                    <Input label={tr('Duration Days')} type="number" value={item.durationDays ? String(item.durationDays) : ''} onChange={(value) => updateMedication(index, { durationDays: parseNumber(value) })} />
                    <Input label={tr('Indication')} value={item.indication || ''} onChange={(value) => updateMedication(index, { indication: value })} />
                    <TextArea label={tr('Instructions')} value={item.instructions || ''} onChange={(value) => updateMedication(index, { instructions: value })} />
                    <ToggleField label={tr('PRN')} checked={Boolean(item.prn)} onChange={(checked) => updateMedication(index, { prn: checked })} />
                  </Row>
                ))}
              </Section>

              <Section title={tr('Follow-Up')} onAdd={() => setDraft((prev) => ({ ...prev, followUps: [...prev.followUps, { modality: '', intervalDays: 30 }] }))}>
                {draft.followUps.map((item, index) => (
                  <Row key={`follow-up-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, followUps: prev.followUps.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Modality')} value={item.modality || ''} onChange={(value) => updateFollowUp(index, { modality: value })} />
                    <Input label={tr('Interval Days')} type="number" value={item.intervalDays ? String(item.intervalDays) : ''} onChange={(value) => updateFollowUp(index, { intervalDays: parseNumber(value) })} />
                    <Input label={tr('Owner Role')} value={item.ownerRole || ''} onChange={(value) => updateFollowUp(index, { ownerRole: value })} />
                    <TextArea label={tr('Purpose')} value={item.purpose || ''} onChange={(value) => updateFollowUp(index, { purpose: value })} />
                  </Row>
                ))}
              </Section>

              <Section title={tr('Education')} onAdd={() => setDraft((prev) => ({ ...prev, education: [...prev.education, { topic: '', objective: '' }] }))}>
                {draft.education.map((item, index) => (
                  <Row key={`education-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, education: prev.education.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Topic')} value={item.topic || ''} onChange={(value) => updateEducation(index, { topic: value })} />
                    <Input label={tr('Objective')} value={item.objective || ''} onChange={(value) => updateEducation(index, { objective: value })} />
                    <Input label={tr('Materials')} value={item.materials || ''} onChange={(value) => updateEducation(index, { materials: value })} />
                    <ToggleField label={tr('Required')} checked={Boolean(item.required)} onChange={(checked) => updateEducation(index, { required: checked })} />
                  </Row>
                ))}
              </Section>

              <Section title={tr('Escalation Rules')} onAdd={() => setDraft((prev) => ({ ...prev, escalationRules: [...prev.escalationRules, { trigger: '', action: '', severity: 'medium' }] }))}>
                {draft.escalationRules.map((item, index) => (
                  <Row key={`escalation-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, escalationRules: prev.escalationRules.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Trigger')} value={item.trigger || ''} onChange={(value) => updateEscalationRule(index, { trigger: value })} />
                    <Input label={tr('Action')} value={item.action || ''} onChange={(value) => updateEscalationRule(index, { action: value })} />
                    <Select label={tr('Severity')} value={item.severity || 'medium'} options={['low', 'medium', 'high']} onChange={(value) => updateEscalationRule(index, { severity: value as 'low' | 'medium' | 'high' })} />
                  </Row>
                ))}
              </Section>

              <Section title={tr('Care Team')} onAdd={() => setDraft((prev) => ({ ...prev, careTeam: [...prev.careTeam, { role: '', memberName: '' }] }))}>
                {draft.careTeam.map((item, index) => (
                  <Row key={`care-team-${index}`} onRemove={() => setDraft((prev) => ({ ...prev, careTeam: prev.careTeam.filter((_, itemIndex) => itemIndex !== index) }))}>
                    <Input label={tr('Role')} value={item.role || ''} onChange={(value) => updateCareTeamMember(index, { role: value })} />
                    <Input label={tr('Member Name')} value={item.memberName || ''} onChange={(value) => updateCareTeamMember(index, { memberName: value })} />
                    <Input label={tr('Responsibility')} value={item.responsibility || ''} onChange={(value) => updateCareTeamMember(index, { responsibility: value })} />
                    <Input label={tr('Contact')} value={item.contact || ''} onChange={(value) => updateCareTeamMember(index, { contact: value })} />
                  </Row>
                ))}
              </Section>
            </div>
          </details>

          <button className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm" onClick={saveDraftLocally}>
            <Save size={14} /> {tr('Save This Template Locally')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-sm text-slate-500">{label}</span>
      <input className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white" value={value} onChange={(event) => onChange(event.target.value)} type={type} />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-sm text-slate-500">{label}</span>
      <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white min-h-[84px]" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{detail}</div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { tr } = useI18n();
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h5 className="text-sm font-semibold text-slate-800">{title}</h5>
        <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700" onClick={onAdd}>
          <Plus size={12} /> {tr('Add')}
        </button>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ children, onRemove }: { children: ReactNode; onRemove: () => void }) {
  const { tr } = useI18n();
  return (
    <div className="rounded-lg border border-slate-200 p-2 space-y-2">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{children}</div>
      <button className="text-xs text-rose-700" onClick={onRemove}>
        {tr('Remove row')}
      </button>
    </div>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex min-h-[42px] items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  const { tr } = useI18n();
  return (
    <label className="space-y-1 block">
      <span className="text-sm text-slate-500">{label}</span>
      <SelectMenu
        value={value}
        onChange={onChange}
        buttonAriaLabel={label}
        triggerClassName="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
        options={options.map((option) => ({ value: option, label: tr(option) }))}
      />
    </label>
  );
}
